import React from "react";
import { Link } from "react-router-dom";
import { MessageCircle, Phone, MapPin, Clock, ShieldCheck, Award } from "lucide-react";
import ChatWidget from "../components/ChatWidget";

export default function Landing() {
  return (
    <div style={{ minHeight: "100vh", background: "#f5f7fb", color: "#0C2747" }}>
      {/* Nav */}
      <nav
        data-testid="landing-nav"
        style={{
          background: "#0C2747",
          color: "#fff",
          padding: "14px 24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "2px solid #C49A45",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 999,
              background: "linear-gradient(160deg, #C49A45, #e2b95b)",
              color: "#0C2747",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: "'Playfair Display', serif",
              fontWeight: 700,
              fontSize: 20,
            }}
          >
            S
          </div>
          <div>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 700, lineHeight: 1 }}>
              Srinidhi
            </div>
            <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }}>Dental & Implant Clinic · Chembur Naka</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Link
            data-testid="nav-chat"
            to="/chat"
            style={{
              color: "#fff",
              background: "#1b53a6",
              padding: "8px 16px",
              borderRadius: 999,
              textDecoration: "none",
              fontSize: 13,
              fontWeight: 500,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <MessageCircle size={14} /> Open chat
          </Link>
          <Link
            data-testid="nav-admin"
            to="/admin/login"
            style={{
              color: "#e2b95b",
              background: "transparent",
              padding: "8px 12px",
              borderRadius: 999,
              textDecoration: "none",
              fontSize: 13,
              border: "1px solid rgba(226,185,91,0.4)",
            }}
          >
            Admin
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section style={{ maxWidth: 1120, margin: "0 auto", padding: "56px 24px 40px", display: "grid", gap: 40, gridTemplateColumns: "1fr" }}>
        <div style={{ display: "grid", gap: 40, gridTemplateColumns: "1.05fr 0.95fr", alignItems: "center" }} className="landing-hero-grid">
          <div>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                background: "#fff",
                border: "1px solid #e5eaf1",
                borderRadius: 999,
                padding: "6px 14px",
                fontSize: 12,
                color: "#0C2747",
              }}
            >
              <span style={{ width: 8, height: 8, borderRadius: 999, background: "#22c55e" }}></span>
              Chatbot demo — website assistant for srinidhidental.com
            </div>
            <h1
              className="font-display"
              style={{ fontSize: 46, lineHeight: 1.05, margin: "18px 0 12px", color: "#0C2747", fontWeight: 700 }}
            >
              An assistant that answers, gently — and books.
            </h1>
            <p style={{ fontSize: 16, color: "#475569", lineHeight: 1.6, margin: 0, maxWidth: 520 }}>
              A compliance-first chat assistant for Srinidhi Dental & Implant Clinic, grounded strictly in
              the clinic's knowledge base. It quotes indicative fees with the required disclaimer, never
              diagnoses, escalates emergencies, and captures appointment requests for the team to confirm.
            </p>
            <div style={{ display: "flex", gap: 12, marginTop: 24, flexWrap: "wrap" }}>
              <Link
                data-testid="hero-open-chat"
                to="/chat"
                style={{
                  background: "#1b53a6",
                  color: "#fff",
                  padding: "12px 22px",
                  borderRadius: 999,
                  textDecoration: "none",
                  fontWeight: 500,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <MessageCircle size={16} /> Try the chat
              </Link>
              <a
                href="tel:+919321636840"
                style={{
                  background: "transparent",
                  color: "#0C2747",
                  padding: "12px 20px",
                  borderRadius: 999,
                  textDecoration: "none",
                  fontWeight: 500,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  border: "1px solid #C49A45",
                }}
              >
                <Phone size={16} /> +91 93216 36840
              </a>
            </div>
          </div>

          {/* Preview card */}
          <div
            style={{
              background: "#fff",
              border: "1px solid #e5eaf1",
              borderRadius: 20,
              padding: 24,
              boxShadow: "0 24px 60px rgba(12, 39, 71, 0.10)",
            }}
          >
            <div style={{ display: "grid", gap: 14 }}>
              <FeatureRow icon={<Award size={18} />} title="Grounded in a 133-Q&A knowledge base" body="Answers only from the clinic's approved document — never invents fees or capabilities." />
              <FeatureRow icon={<ShieldCheck size={18} />} title="Compliance-first" body="Pricing disclaimer on every fee, no diagnosis, no discounts, no superlatives — by design." />
              <FeatureRow icon={<Clock size={18} />} title="Emergency detection" body="Trauma, swelling and severe symptoms trigger the clinic emergency block instantly." />
              <FeatureRow icon={<MapPin size={18} />} title="Multilingual" body="English · हिन्दी · मराठी · தமிழ் — replies in the patient's language." />
            </div>
          </div>
        </div>
      </section>

      {/* Embed instructions */}
      <section style={{ background: "#0C2747", color: "#fff", padding: "48px 24px" }}>
        <div style={{ maxWidth: 1120, margin: "0 auto", display: "grid", gap: 32, gridTemplateColumns: "1fr 1fr" }} className="landing-embed-grid">
          <div>
            <div style={{ color: "#C49A45", fontSize: 12, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 8 }}>
              Embeddable widget
            </div>
            <h2 className="font-display" style={{ fontSize: 30, fontWeight: 700, margin: 0, lineHeight: 1.15 }}>
              One script tag — floating chat everywhere on srinidhidental.com
            </h2>
            <p style={{ color: "#c9d3e4", lineHeight: 1.6, marginTop: 12 }}>
              Drop the snippet on the right into the static site. It injects an iframe that hosts the widget
              in isolation, so it never touches the host page's styles or scripts.
            </p>
            <div style={{ display: "flex", gap: 8, marginTop: 18, flexWrap: "wrap" }}>
              <Link to="/chat" data-testid="embed-try-chat" style={{ color: "#0C2747", background: "#C49A45", padding: "10px 18px", borderRadius: 999, fontWeight: 500, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6 }}>
                <MessageCircle size={14} /> Try the chat
              </Link>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/`}
                target="_blank"
                rel="noreferrer"
                style={{ color: "#e2b95b", padding: "10px 18px", borderRadius: 999, border: "1px solid rgba(226,185,91,0.35)", textDecoration: "none" }}
              >
                Check API status →
              </a>
            </div>
          </div>
          <div>
            <div style={{ background: "#081c33", border: "1px solid #163a5f", borderRadius: 14, padding: 20, fontFamily: "Menlo, Monaco, monospace", fontSize: 13, color: "#dbeafe", overflowX: "auto" }}>
{`<!-- Paste before </body> on srinidhidental.com -->
<script src="${process.env.REACT_APP_BACKEND_URL || "https://your-domain"}/embed.js" async></script>`}
            </div>
            <div style={{ marginTop: 12, fontSize: 12, color: "#94a3b8" }}>
              The script points to the widget iframe at <code style={{ color: "#e2b95b" }}>/embed</code>, so
              nothing on the host page can be interfered with.
            </div>
          </div>
        </div>
      </section>

      <footer style={{ padding: "32px 24px", textAlign: "center", color: "#64748B", fontSize: 12 }}>
        Srinidhi Dental &amp; Implant Clinic · B-54, Gurudev Apartments, Chembur Naka, Mumbai · +91 93216 36840
      </footer>

      <ChatWidget />
    </div>
  );
}

function FeatureRow({ icon, title, body }) {
  return (
    <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
      <div
        style={{
          width: 34,
          height: 34,
          borderRadius: 10,
          background: "#f0f5ff",
          color: "#1b53a6",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <div>
        <div style={{ fontWeight: 600, fontSize: 14 }}>{title}</div>
        <div style={{ color: "#64748B", fontSize: 13, lineHeight: 1.5, marginTop: 2 }}>{body}</div>
      </div>
    </div>
  );
}
